import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Image, FolderTree, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import CloudinaryUpload from "@/components/CloudinaryUpload";

const AdminContent = () => {
  const { getAuthHeaders } = useAuth();
  const [sliders, setSliders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sliderDialogOpen, setSliderDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sliderForm, setSliderForm] = useState({
    title: "",
    subtitle: "",
    image_url: "",
    link: "",
    order: 0,
  });
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    icon: "",
    image_url: "",
    order: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sliderRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/sliders`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/categories`, { headers: getAuthHeaders() }),
      ]);
      setSliders(sliderRes.data.sliders || []);
      setCategories(catRes.data.categories || []);
    } catch (error) {
      toast.error("Failed to load content");
    }
    setLoading(false);
  };

  const createSlider = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/sliders`, sliderForm, {
        headers: getAuthHeaders(),
      });
      toast.success("Slider created!");
      setSliderDialogOpen(false);
      setSliderForm({ title: "", subtitle: "", image_url: "", link: "", order: 0 });
      fetchData();
    } catch (error) {
      toast.error("Failed to create slider");
    }
    setSubmitting(false);
  };

  const deleteSlider = async (sliderId) => {
    if (!confirm("Delete this slider?")) return;
    try {
      await axios.delete(`${API}/admin/sliders/${sliderId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Slider deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete slider");
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/categories`, categoryForm, {
        headers: getAuthHeaders(),
      });
      toast.success("Category created!");
      setCategoryDialogOpen(false);
      setCategoryForm({ name: "", icon: "", image_url: "", order: 0 });
      fetchData();
    } catch (error) {
      toast.error("Failed to create category");
    }
    setSubmitting(false);
  };

  const deleteCategory = async (categoryId) => {
    if (!confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const iconOptions = [
    "Shirt", "Smartphone", "Plane", "UtensilsCrossed",
    "Heart", "Sparkles", "Home", "Tv", "ShoppingBag",
    "Gift", "Car", "Book", "Music", "Camera"
  ];

  if (loading) {
    return (
      <AdminLayout title="Content Management">
        <div className="animate-pulse space-y-4">
          <div className="h-40 bg-gray-200 rounded-2xl" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Content Management">
      <Tabs defaultValue="sliders" className="space-y-6">
        <TabsList>
          <TabsTrigger value="sliders">
            <Image className="w-4 h-4 mr-2" />
            Sliders
          </TabsTrigger>
          <TabsTrigger value="categories">
            <FolderTree className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sliders">
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-500">{sliders.length} sliders</p>
            <Dialog open={sliderDialogOpen} onOpenChange={setSliderDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400" data-testid="add-slider-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Slider
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Slider</DialogTitle>
                </DialogHeader>
                <form onSubmit={createSlider} className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={sliderForm.title}
                      onChange={(e) => setSliderForm({ ...sliderForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={sliderForm.subtitle}
                      onChange={(e) => setSliderForm({ ...sliderForm, subtitle: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Slider Image</Label>
                    <CloudinaryUpload
                      value={sliderForm.image_url}
                      onChange={(url) => setSliderForm({ ...sliderForm, image_url: url })}
                      folder="sliders/"
                      resourceType="image"
                      testId="slider-image-upload"
                    />
                  </div>
                  <div>
                    <Label>Link (Optional)</Label>
                    <Input
                      value={sliderForm.link}
                      onChange={(e) => setSliderForm({ ...sliderForm, link: e.target.value })}
                      placeholder="/products/fashion"
                    />
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input
                      type="number"
                      value={sliderForm.order}
                      onChange={(e) => setSliderForm({ ...sliderForm, order: parseInt(e.target.value) })}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                    disabled={submitting}
                  >
                    {submitting ? "Creating..." : "Create Slider"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sliders.map((slider) => (
              <Card key={slider.id} className="border-0 shadow-sm overflow-hidden">
                <div className="aspect-video bg-gray-100 relative">
                  <img
                    src={slider.image_url}
                    alt={slider.title}
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-2 right-2 bg-black/50 text-white">
                    #{slider.order}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold">{slider.title}</h3>
                  {slider.subtitle && (
                    <p className="text-sm text-gray-500">{slider.subtitle}</p>
                  )}
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => deleteSlider(slider.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {sliders.length === 0 && (
              <div className="col-span-full text-center py-12 bg-gray-50 rounded-2xl">
                <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No sliders yet</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-500">{categories.length} categories</p>
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400" data-testid="add-category-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Category</DialogTitle>
                </DialogHeader>
                <form onSubmit={createCategory} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Icon (Lucide icon name)</Label>
                    <select
                      className="w-full border rounded-md p-2"
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                      required
                    >
                      <option value="">Select icon</option>
                      {iconOptions.map((icon) => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Category Image (Optional)</Label>
                    <CloudinaryUpload
                      value={categoryForm.image_url}
                      onChange={(url) => setCategoryForm({ ...categoryForm, image_url: url })}
                      folder="uploads/"
                      resourceType="image"
                      testId="category-image-upload"
                    />
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input
                      type="number"
                      value={categoryForm.order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) })}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                    disabled={submitting}
                  >
                    {submitting ? "Creating..." : "Create Category"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Card key={cat.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                        <span className="text-primary-600 text-xs font-bold">{cat.icon?.slice(0, 2)}</span>
                      </div>
                      <div>
                        <p className="font-medium">{cat.name}</p>
                        <p className="text-xs text-gray-400">Order: {cat.order}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => deleteCategory(cat.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {categories.length === 0 && (
              <div className="col-span-full text-center py-12 bg-gray-50 rounded-2xl">
                <FolderTree className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No categories yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminContent;
